// Kinematics Calculators helpers: velocity, acceleration, the three SUVAT
// equations of motion, free fall, projectile motion, centripetal
// acceleration, relative velocity, stopping/braking distance, terminal
// velocity, and a general "pick any 3 of the 5 SUVAT variables" solver.
//
// Scope notes (deliberate narrowing, matching this project's convention):
// - No air resistance in free-fall/projectile motion (ideal kinematics).
// - Square-root solutions (e.g. SUVAT third equation solving for u or v,
//   or the general solver) always take the positive root - the ambiguous
//   negative-root case is out of scope, same narrowing used for the
//   trigonometry Law of Sines/Cosines calculators.
// - g = 9.81 m/s^2 (standard gravity) is hardcoded throughout.
import { t } from './i18n'

const G = 9.81 // m/s^2, standard gravity

const NO_SOLUTION_LABEL: Record<string, string> = {
    en: 'No solution', ru: 'Решения нет', lv: 'Nav risinājuma', pl: 'Brak rozwiązania',
    es: 'Sin solución', fr: 'Pas de solution', it: 'Nessuna soluzione', de: 'Keine Lösung',
}

// ============================================================
// 1. Velocity Calculator (v = d/t)
// ============================================================
export function velocityCalculator(params: { solve_for: unknown; distance_m: unknown; time_s: unknown; velocity_ms: unknown; language: unknown }): {
    result: number | string; steps: string
} {
    const solveFor = String(params.solve_for || 'velocity')
    const d = Number(params.distance_m) || 0
    const tm = Number(params.time_s) || 0
    const v = Number(params.velocity_ms) || 0
    const errText = t(NO_SOLUTION_LABEL, params.language)

    if (solveFor === 'distance') {
        return { result: v * tm, steps: `d = v × t = ${v} × ${tm} = ${(v * tm).toFixed(4)} m` }
    }
    if (solveFor === 'time') {
        if (v === 0) return { result: errText, steps: errText }
        return { result: d / v, steps: `t = d / v = ${d} / ${v} = ${(d / v).toFixed(4)} s` }
    }
    if (tm === 0) return { result: errText, steps: errText }
    return { result: d / tm, steps: `v = d / t = ${d} / ${tm} = ${(d / tm).toFixed(4)} m/s` }
}

// ============================================================
// 2. Acceleration Calculator (a = (v-u)/t)
// ============================================================
export function accelerationCalculator(params: {
    solve_for: unknown; initial_velocity: unknown; final_velocity: unknown; time_s: unknown; acceleration: unknown; language: unknown
}): { result: number | string; steps: string } {
    const solveFor = String(params.solve_for || 'acceleration')
    const u = Number(params.initial_velocity) || 0
    const v = Number(params.final_velocity) || 0
    const tm = Number(params.time_s) || 0
    const a = Number(params.acceleration) || 0
    const errText = t(NO_SOLUTION_LABEL, params.language)

    if (solveFor === 'final_velocity') {
        return { result: u + a * tm, steps: `v = u + at = ${u} + ${a} × ${tm} = ${(u + a * tm).toFixed(4)} m/s` }
    }
    if (solveFor === 'initial_velocity') {
        return { result: v - a * tm, steps: `u = v - at = ${v} - ${a} × ${tm} = ${(v - a * tm).toFixed(4)} m/s` }
    }
    if (solveFor === 'time') {
        if (a === 0) return { result: errText, steps: errText }
        return { result: (v - u) / a, steps: `t = (v - u) / a = (${v} - ${u}) / ${a} = ${((v - u) / a).toFixed(4)} s` }
    }
    if (tm === 0) return { result: errText, steps: errText }
    return { result: (v - u) / tm, steps: `a = (v - u) / t = (${v} - ${u}) / ${tm} = ${((v - u) / tm).toFixed(4)} m/s²` }
}

// ============================================================
// 3. Displacement Calculator (s = ut + 1/2 at^2), also returns v = u + at
// ============================================================
export function displacementCalculator(params: { initial_velocity: unknown; acceleration: unknown; time_s: unknown; language: unknown }): {
    displacement: number; final_velocity: number; steps: string
} {
    const u = Number(params.initial_velocity) || 0
    const a = Number(params.acceleration) || 0
    const tm = Math.max(Number(params.time_s) || 0, 0)

    const s = u * tm + 0.5 * a * tm * tm
    const v = u + a * tm

    const steps = [
        `s = ut + ½at² = (${u} × ${tm}) + (0.5 × ${a} × ${tm}²) = ${s.toFixed(4)} m`,
        `v = u + at = ${u} + (${a} × ${tm}) = ${v.toFixed(4)} m/s`,
    ].join('\n')

    return { displacement: s, final_velocity: v, steps }
}

// ============================================================
// 4. Final Velocity Calculator (v = u + at)
// ============================================================
export function finalVelocityCalculator(params: {
    solve_for: unknown; initial_velocity: unknown; acceleration: unknown; time_s: unknown; final_velocity: unknown; language: unknown
}): { result: number | string; steps: string } {
    const solveFor = String(params.solve_for || 'v')
    const u = Number(params.initial_velocity) || 0
    const a = Number(params.acceleration) || 0
    const tm = Number(params.time_s) || 0
    const v = Number(params.final_velocity) || 0
    const errText = t(NO_SOLUTION_LABEL, params.language)

    if (solveFor === 'u') {
        return { result: v - a * tm, steps: `u = v - at = ${v} - (${a} × ${tm}) = ${(v - a * tm).toFixed(4)} m/s` }
    }
    if (solveFor === 'a') {
        if (tm === 0) return { result: errText, steps: errText }
        return { result: (v - u) / tm, steps: `a = (v - u) / t = (${v} - ${u}) / ${tm} = ${((v - u) / tm).toFixed(4)} m/s²` }
    }
    if (solveFor === 't') {
        if (a === 0) return { result: errText, steps: errText }
        return { result: (v - u) / a, steps: `t = (v - u) / a = (${v} - ${u}) / ${a} = ${((v - u) / a).toFixed(4)} s` }
    }
    return { result: u + a * tm, steps: `v = u + at = ${u} + (${a} × ${tm}) = ${(u + a * tm).toFixed(4)} m/s` }
}

// ============================================================
// 5. SUVAT Third Equation Calculator (v^2 = u^2 + 2as)
// ============================================================
export function suvatThirdEquationCalculator(params: {
    solve_for: unknown; initial_velocity: unknown; final_velocity: unknown; acceleration: unknown; displacement: unknown; language: unknown
}): { result: number | string; steps: string } {
    const solveFor = String(params.solve_for || 'v')
    const u = Number(params.initial_velocity) || 0
    const v = Number(params.final_velocity) || 0
    const a = Number(params.acceleration) || 0
    const s = Number(params.displacement) || 0
    const errText = t(NO_SOLUTION_LABEL, params.language)

    if (solveFor === 'u') {
        const disc = v * v - 2 * a * s
        if (disc < 0) return { result: errText, steps: errText }
        const result = Math.sqrt(disc)
        return { result, steps: `u = √(v² - 2as) = √(${v}² - 2×${a}×${s}) = ${result.toFixed(4)} m/s` }
    }
    if (solveFor === 'a') {
        if (s === 0) return { result: errText, steps: errText }
        const result = (v * v - u * u) / (2 * s)
        return { result, steps: `a = (v² - u²) / 2s = (${v}² - ${u}²) / (2×${s}) = ${result.toFixed(4)} m/s²` }
    }
    if (solveFor === 's') {
        if (a === 0) return { result: errText, steps: errText }
        const result = (v * v - u * u) / (2 * a)
        return { result, steps: `s = (v² - u²) / 2a = (${v}² - ${u}²) / (2×${a}) = ${result.toFixed(4)} m` }
    }
    const disc = u * u + 2 * a * s
    if (disc < 0) return { result: errText, steps: errText }
    const result = Math.sqrt(disc)
    return { result, steps: `v = √(u² + 2as) = √(${u}² + 2×${a}×${s}) = ${result.toFixed(4)} m/s` }
}

// ============================================================
// 6. Free Fall Calculator
// ============================================================
export function freeFallCalculator(params: { mode: unknown; value: unknown; language: unknown }): {
    time_s: number; distance_fallen_m: number; final_velocity_ms: number
} {
    const mode = String(params.mode || 'from_time')
    const value = Math.max(Number(params.value) || 0, 0)

    if (mode === 'from_height') {
        const height = value
        const time = Math.sqrt((2 * height) / G)
        const velocity = Math.sqrt(2 * G * height)
        return { time_s: time, distance_fallen_m: height, final_velocity_ms: velocity }
    }
    const time = value
    const distance = 0.5 * G * time * time
    const velocity = G * time
    return { time_s: time, distance_fallen_m: distance, final_velocity_ms: velocity }
}

// ============================================================
// 7. Projectile Motion Calculator
// ============================================================
export function projectileMotionCalculator(params: { initial_speed_ms: unknown; launch_angle_deg: unknown; initial_height_m: unknown; language: unknown }): {
    range_m: number; max_height_m: number; time_of_flight_s: number
} {
    const speed = Math.max(Number(params.initial_speed_ms) || 0, 0)
    const angleDeg = Number(params.launch_angle_deg) || 0
    const h0 = Math.max(Number(params.initial_height_m) || 0, 0)
    const rad = (angleDeg * Math.PI) / 180
    const vx = speed * Math.cos(rad)
    const vy = speed * Math.sin(rad)

    const disc = vy * vy + 2 * G * h0
    const timeOfFlight = (vy + Math.sqrt(Math.max(disc, 0))) / G
    const maxHeight = h0 + (vy * vy) / (2 * G)
    const range = vx * timeOfFlight

    return { range_m: range, max_height_m: maxHeight, time_of_flight_s: timeOfFlight }
}

// ============================================================
// 8. Centripetal Acceleration Calculator (a = v^2 / r)
// ============================================================
export function centripetalAccelerationCalculator(params: { solve_for: unknown; velocity_ms: unknown; radius_m: unknown; acceleration_ms2: unknown; language: unknown }): {
    result: number | string; steps: string
} {
    const solveFor = String(params.solve_for || 'acceleration')
    const v = Number(params.velocity_ms) || 0
    const r = Math.max(Number(params.radius_m) || 0.0001, 0.0001)
    const a = Number(params.acceleration_ms2) || 0
    const errText = t(NO_SOLUTION_LABEL, params.language)

    if (solveFor === 'velocity') {
        if (a < 0) return { result: errText, steps: errText }
        const result = Math.sqrt(a * r)
        return { result, steps: `v = √(a × r) = √(${a} × ${r}) = ${result.toFixed(4)} m/s` }
    }
    if (solveFor === 'radius') {
        if (a === 0) return { result: errText, steps: errText }
        const result = (v * v) / a
        return { result, steps: `r = v² / a = ${v}² / ${a} = ${result.toFixed(4)} m` }
    }
    const result = (v * v) / r
    return { result, steps: `a = v² / r = ${v}² / ${r} = ${result.toFixed(4)} m/s²` }
}

// ============================================================
// 9. Relative Velocity Calculator (1D, signed velocities)
// ============================================================
export function relativeVelocityCalculator(params: { velocity_a_ms: unknown; velocity_b_ms: unknown; language: unknown }): {
    relative_velocity_ms: number; steps: string
} {
    const vA = Number(params.velocity_a_ms) || 0
    const vB = Number(params.velocity_b_ms) || 0
    const result = vA - vB
    return { relative_velocity_ms: result, steps: `v(A relative to B) = vA - vB = ${vA} - (${vB}) = ${result.toFixed(4)} m/s` }
}

// ============================================================
// 10. Stopping / Braking Distance Calculator
// ============================================================
export function stoppingDistanceCalculator(params: { initial_velocity_ms: unknown; deceleration_ms2: unknown; reaction_time_s: unknown; language: unknown }): {
    reaction_distance_m: number; braking_distance_m: number; total_stopping_distance_m: number; steps: string
} {
    const u = Math.max(Number(params.initial_velocity_ms) || 0, 0)
    const decel = Math.max(Number(params.deceleration_ms2) || 0.0001, 0.0001)
    const reactionTime = Math.max(Number(params.reaction_time_s) || 0, 0)

    const reactionDistance = u * reactionTime
    const brakingDistance = (u * u) / (2 * decel)
    const total = reactionDistance + brakingDistance

    const steps = [
        `Reaction distance = u × t_reaction = ${u} × ${reactionTime} = ${reactionDistance.toFixed(4)} m`,
        `Braking distance = u² / 2a = ${u}² / (2×${decel}) = ${brakingDistance.toFixed(4)} m`,
        `Total stopping distance = ${reactionDistance.toFixed(4)} + ${brakingDistance.toFixed(4)} = ${total.toFixed(4)} m`,
    ].join('\n')

    return { reaction_distance_m: reactionDistance, braking_distance_m: brakingDistance, total_stopping_distance_m: total, steps }
}

// ============================================================
// 11. Terminal Velocity Calculator
// ============================================================
export function terminalVelocityCalculator(params: { mass_kg: unknown; drag_coefficient: unknown; cross_sectional_area_m2: unknown; air_density_kgm3: unknown; language: unknown }): {
    terminal_velocity_ms: number
} {
    const mass = Math.max(Number(params.mass_kg) || 0.0001, 0.0001)
    const cd = Math.max(Number(params.drag_coefficient) || 0.0001, 0.0001)
    const area = Math.max(Number(params.cross_sectional_area_m2) || 0.0001, 0.0001)
    const density = Math.max(Number(params.air_density_kgm3) || 0.0001, 0.0001)

    const result = Math.sqrt((2 * mass * G) / (density * area * cd))
    return { terminal_velocity_ms: result }
}

// ============================================================
// 12. General Kinematics Equations Solver (pick any 3 of 5 SUVAT variables)
// ============================================================
type KV = 'u' | 'v' | 'a' | 's' | 't'

export function generalKinematicsSolver(params: {
    known_1: unknown; known_1_value: unknown
    known_2: unknown; known_2_value: unknown
    known_3: unknown; known_3_value: unknown
    language: unknown
}): { u: number | string; v: number | string; a: number | string; s: number | string; t: number | string; steps: string } {
    const known: Partial<Record<KV, number>> = {}
    const k1 = String(params.known_1) as KV
    const k2 = String(params.known_2) as KV
    const k3 = String(params.known_3) as KV
    known[k1] = Number(params.known_1_value) || 0
    known[k2] = Number(params.known_2_value) || 0
    known[k3] = Number(params.known_3_value) || 0

    const has = (k: KV) => known[k] !== undefined
    const errText = t(NO_SOLUTION_LABEL, params.language)

    let u = known.u
    let v = known.v
    let a = known.a
    let s = known.s
    let tm = known.t

    try {
        if (has('u') && has('v') && has('a')) {
            if (a === 0) throw new Error('undetermined')
            tm = (v! - u!) / a!
            s = (v! * v! - u! * u!) / (2 * a!)
        } else if (has('u') && has('v') && has('s')) {
            if (v === u) {
                if (u === 0) throw new Error('undetermined')
                a = 0
                tm = s! / u!
            } else {
                a = (v! * v! - u! * u!) / (2 * s!)
                if (a === 0) throw new Error('undetermined')
                tm = (v! - u!) / a
            }
        } else if (has('u') && has('v') && has('t')) {
            if (tm === 0) throw new Error('undetermined')
            a = (v! - u!) / tm!
            s = ((u! + v!) / 2) * tm!
        } else if (has('u') && has('a') && has('s')) {
            const disc = u! * u! + 2 * a! * s!
            if (disc < 0) throw new Error('no solution')
            v = Math.sqrt(disc)
            tm = a !== 0 ? (v - u!) / a! : (u !== 0 ? s! / u! : 0)
        } else if (has('u') && has('a') && has('t')) {
            v = u! + a! * tm!
            s = u! * tm! + 0.5 * a! * tm! * tm!
        } else if (has('u') && has('s') && has('t')) {
            if (tm === 0) throw new Error('undetermined')
            a = (2 * (s! - u! * tm!)) / (tm! * tm!)
            v = u! + a * tm!
        } else if (has('v') && has('a') && has('s')) {
            const disc = v! * v! - 2 * a! * s!
            if (disc < 0) throw new Error('no solution')
            u = Math.sqrt(disc)
            tm = a !== 0 ? (v! - u) / a! : (u !== 0 ? s! / u : 0)
        } else if (has('v') && has('a') && has('t')) {
            u = v! - a! * tm!
            s = v! * tm! - 0.5 * a! * tm! * tm!
        } else if (has('v') && has('s') && has('t')) {
            if (tm === 0) throw new Error('undetermined')
            u = (2 * s!) / tm! - v!
            a = (v! - u) / tm!
        } else if (has('a') && has('s') && has('t')) {
            if (tm === 0) throw new Error('undetermined')
            u = (s! - 0.5 * a! * tm! * tm!) / tm!
            v = u + a! * tm!
        } else {
            throw new Error('invalid combination')
        }
    } catch {
        return { u: errText, v: errText, a: errText, s: errText, t: errText, steps: errText }
    }

    const steps = [
        `u = ${u!.toFixed(4)} m/s`,
        `v = ${v!.toFixed(4)} m/s`,
        `a = ${a!.toFixed(4)} m/s²`,
        `s = ${s!.toFixed(4)} m`,
        `t = ${tm!.toFixed(4)} s`,
    ].join('\n')

    return { u: u!, v: v!, a: a!, s: s!, t: tm!, steps }
}
